import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  X,
  Download,
  CloudUpload,
  RefreshCw,
  Trash2,
  Pencil,
  Check
} from 'lucide-react';

const DEFAULT_STATS = {
  total: 0,
  available: 0,
  reserved: 0,
  used: 0,
  lastUploadedAt: null
};

const STATUS_LABELS = {
  available: 'Available',
  reserved: 'Reserved',
  used: 'Used'
};

const statusBadgeClasses = (status) => {
  switch (status) {
    case 'available':
      return 'bg-emerald-100 text-emerald-700';
    case 'reserved':
      return 'bg-amber-100 text-amber-700';
    case 'used':
      return 'bg-slate-200 text-slate-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const ManageClaimCodes = ({
  open,
  onClose,
  campaignId,
  prize,
  allocation = null,
  api,
  onPrizeUpdated,
  setSuccessMessage,
  setShowSuccess,
  setErrorMessage,
  setShowError,
  className = '',
  variant = 'modal',
  showHeader = true
}) => {
  const [loading, setLoading] = useState(false);
  const [savingStrategy, setSavingStrategy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState('range');
  const [rangeConfig, setRangeConfig] = useState({ prefix: '', start: '', end: '' });
  const [voucherStats, setVoucherStats] = useState(DEFAULT_STATS);
  const [voucherList, setVoucherList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [selectedVoucherIds, setSelectedVoucherIds] = useState(() => new Set());
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [editingVoucherId, setEditingVoucherId] = useState(null);
  const [editingVoucherCode, setEditingVoucherCode] = useState('');
  const [savingVoucherId, setSavingVoucherId] = useState(null);
  const fileInputRef = useRef(null);

  // When an allocation (Quantity Breakdown row) is passed, all voucher actions are scoped to that
  // row's own CSV list and the prize-wide range/list strategy is left alone.
  const allocationId = allocation?.allocation_id || null;
  const isAllocationMode = !!allocationId;
  const allocationQuery = isAllocationMode ? `allocation_id=${encodeURIComponent(allocationId)}` : '';

  const showSuccess = (message) => {
    if (!setSuccessMessage || !setShowSuccess) return;
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const showError = (message) => {
    if (!setErrorMessage || !setShowError) return;
    setErrorMessage(message);
    setShowError(true);
    setTimeout(() => setShowError(false), 3000);
  };

  const resetState = () => {
    setSelectedStrategy('range');
    setRangeConfig({ prefix: '', start: '', end: '' });
    setVoucherStats(DEFAULT_STATS);
    setVoucherList([]);
    setPagination({ page: 1, limit: 10, total: 0, totalPages: 1 });
    setStatusFilter('all');
    setSearchTerm('');
    setReplaceExisting(false);
    setSelectedVoucherIds(new Set());
    setPendingConfirmation(null);
    setEditingVoucherId(null);
    setEditingVoucherCode('');
    setSavingVoucherId(null);
  };

  useEffect(() => {
    if (!open || !prize) {
      resetState();
      return;
    }
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prize?.id, allocationId]);

  const fetchConfig = async () => {
    if (!prize) return;
    setLoading(true);
    setSelectedVoucherIds(new Set());
    setPendingConfirmation(null);
    try {
      const { data } = await api.get(
        `/admin/campaigns/${campaignId}/instant-win/prizes/${prize.id}/claim-codes${allocationQuery ? `?${allocationQuery}` : ''}`
      );
      const prizeData = data.prize || prize;

      setSelectedStrategy(isAllocationMode ? 'list' : (prizeData.claim_code_strategy || 'range'));
      setRangeConfig({
        prefix: prizeData.claim_code_prefix || '',
        start: prizeData.range_start !== null && prizeData.range_start !== undefined
          ? String(prizeData.range_start)
          : '',
        end: prizeData.range_end !== null && prizeData.range_end !== undefined
          ? String(prizeData.range_end)
          : ''
      });
      setVoucherStats({
        total: data.voucherStats?.total || 0,
        available: data.voucherStats?.available || 0,
        reserved: data.voucherStats?.reserved || 0,
        used: data.voucherStats?.used || 0,
        lastUploadedAt: data.voucherStats?.lastUploadedAt || null
      });

      if (!isAllocationMode && typeof onPrizeUpdated === 'function') {
        onPrizeUpdated(prizeData);
      }

      if (isAllocationMode || (prizeData.claim_code_strategy || 'range') === 'list') {
        await fetchVoucherList(1, statusFilter, searchTerm);
      } else {
        setVoucherList([]);
        setPagination((prev) => ({ ...prev, page: 1, total: 0, totalPages: 1 }));
      }
    } catch (error) {
      console.error('Failed to load claim code configuration:', error);
      showError(`Failed to load claim code settings: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchVoucherList = async (page = 1, status = statusFilter, search = searchTerm) => {
    if (!prize) return;
    setListLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pagination.limit)
      });
      if (status && status !== 'all') {
        params.append('status', status);
      }
      if (search) {
        params.append('search', search.trim());
      }
      if (isAllocationMode) {
        params.append('allocation_id', allocationId);
      }

      const { data } = await api.get(
        `/admin/campaigns/${campaignId}/instant-win/prizes/${prize.id}/vouchers?${params.toString()}`
      );

      const vouchers = data.vouchers || [];
      setVoucherList(vouchers);
      setSelectedVoucherIds(new Set());
      setEditingVoucherId(null);
      setEditingVoucherCode('');
      setSavingVoucherId(null);
      if (data.pagination) {
        setPagination({
          page: data.pagination.page || page,
          limit: data.pagination.limit || pagination.limit,
          total: data.pagination.total || (data.vouchers?.length || 0),
          totalPages: data.pagination.totalPages || 1
        });
      } else {
        setPagination((prev) => ({
          ...prev,
          page,
          total: data.vouchers?.length || 0,
          totalPages: 1
        }));
      }

      if (data.voucherStats) {
        setVoucherStats({
          total: data.voucherStats.total || 0,
          available: data.voucherStats.available || 0,
          reserved: data.voucherStats.reserved || 0,
          used: data.voucherStats.used || 0,
          lastUploadedAt: data.voucherStats.lastUploadedAt || null
        });
      }

      if (!isAllocationMode && data.prize && typeof onPrizeUpdated === 'function') {
        onPrizeUpdated(data.prize);
      }
    } catch (error) {
      console.error('Failed to load vouchers:', error);
      showError(`Failed to load vouchers: ${error.response?.data?.error || error.message}`);
    } finally {
      setListLoading(false);
    }
  };

  const handleStrategySave = async () => {
    if (!prize) return;
    if (selectedStrategy === 'range') {
      const prefix = rangeConfig.prefix.trim();
      const start = Number(rangeConfig.start);
      const end = Number(rangeConfig.end);
      if (!prefix || !Number.isFinite(start) || !Number.isFinite(end) || start <= 0 || end < start) {
        showError('Please provide a valid prefix and number range.');
        return;
      }
    }

    setSavingStrategy(true);
    try {
      const payload = {
        strategy: selectedStrategy
      };

      if (selectedStrategy === 'range') {
        payload.claim_code_prefix = rangeConfig.prefix.trim().toUpperCase();
        payload.range_start = Number(rangeConfig.start);
        payload.range_end = Number(rangeConfig.end);
      }

      const { data } = await api.put(
        `/admin/campaigns/${campaignId}/instant-win/prizes/${prize.id}/claim-codes`,
        payload
      );

      if (data.prize) {
        setSelectedStrategy(data.prize.claim_code_strategy || selectedStrategy);
        setRangeConfig({
          prefix: data.prize.claim_code_prefix || '',
          start: data.prize.range_start !== null && data.prize.range_start !== undefined
            ? String(data.prize.range_start)
            : '',
          end: data.prize.range_end !== null && data.prize.range_end !== undefined
            ? String(data.prize.range_end)
            : ''
        });
        if (typeof onPrizeUpdated === 'function') {
          onPrizeUpdated(data.prize);
        }
      }

      if (data.voucherStats) {
        setVoucherStats({
          total: data.voucherStats.total || 0,
          available: data.voucherStats.available || 0,
          reserved: data.voucherStats.reserved || 0,
          used: data.voucherStats.used || 0,
          lastUploadedAt: data.voucherStats.lastUploadedAt || null
        });
      }

      showSuccess('Claim code settings saved successfully!');
    } catch (error) {
      console.error('Failed to save claim code settings:', error);
      showError(`Failed to save claim code settings: ${error.response?.data?.error || error.message}`);
    } finally {
      setSavingStrategy(false);
    }
  };

  const toggleSelectVoucher = (voucherId) => {
    setSelectedVoucherIds((prev) => {
      const next = new Set(prev);
      if (next.has(voucherId)) {
        next.delete(voucherId);
      } else {
        next.add(voucherId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedVoucherIds((prev) => {
      if (voucherList.length === 0) return new Set();
      const isAllSelected = voucherList.every(voucher => prev.has(voucher.id));
      if (isAllSelected) {
        return new Set();
      }
      return new Set(voucherList.map(voucher => voucher.id));
    });
  };

  const requestDeleteSelected = () => {
    if (selectedVoucherIds.size === 0) return;
    setPendingConfirmation({
      type: 'delete',
      ids: Array.from(selectedVoucherIds)
    });
  };

  const requestClearVouchers = (scope) => {
    setPendingConfirmation({ type: 'clear', scope });
  };

  const closeConfirmation = () => {
    setPendingConfirmation(null);
  };

  const handleConfirmAction = async () => {
    if (!pendingConfirmation) return;

    if (pendingConfirmation.type === 'clear') {
      await handleClearVouchers(pendingConfirmation.scope || 'all');
      setPendingConfirmation(null);
      return;
    }

    if (pendingConfirmation.type === 'delete') {
      await handleDeleteSelectedVouchers(pendingConfirmation.ids || []);
      setPendingConfirmation(null);
    }
  };

  const handleVoucherUpload = async (file) => {
    if (!file || !prize) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (replaceExisting) {
        formData.append('replace_existing', 'true');
      }
      if (isAllocationMode) {
        formData.append('allocation_id', allocationId);
      }

      const { data } = await api.post(
        `/admin/campaigns/${campaignId}/instant-win/prizes/${prize.id}/vouchers/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (data.prize && !isAllocationMode) {
        setSelectedStrategy(data.prize.claim_code_strategy || 'list');
        if (typeof onPrizeUpdated === 'function') {
          onPrizeUpdated(data.prize);
        }
      }

      if (data.voucherStats) {
        setVoucherStats({
          total: data.voucherStats.total || 0,
          available: data.voucherStats.available || 0,
          reserved: data.voucherStats.reserved || 0,
          used: data.voucherStats.used || 0,
          lastUploadedAt: data.voucherStats.lastUploadedAt || null
        });
      }

      await fetchVoucherList(1, statusFilter, searchTerm);

      const inserted = data.inserted || 0;
      const skipped = data.skipped || 0;
      showSuccess(
        `Uploaded ${inserted} voucher${inserted === 1 ? '' : 's'}${skipped ? ` (${skipped} skipped)` : ''}.`
      );
    } catch (error) {
      console.error('Failed to upload vouchers:', error);
      showError(`Failed to upload vouchers: ${error.response?.data?.error || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleClearVouchers = async (scope = 'all') => {
    if (!prize) return;
    setClearing(true);
    try {
      const queryParts = [];
      if (scope && scope !== 'all') queryParts.push(`status=${scope}`);
      if (isAllocationMode) queryParts.push(allocationQuery);
      const query = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
      const { data } = await api.delete(
        `/admin/campaigns/${campaignId}/instant-win/prizes/${prize.id}/vouchers${query}`
      );

      if (data.prize && !isAllocationMode) {
        if (typeof onPrizeUpdated === 'function') {
          onPrizeUpdated(data.prize);
        }
      }

      if (data.voucherStats) {
        setVoucherStats({
          total: data.voucherStats.total || 0,
          available: data.voucherStats.available || 0,
          reserved: data.voucherStats.reserved || 0,
          used: data.voucherStats.used || 0,
          lastUploadedAt: data.voucherStats.lastUploadedAt || null
        });
      } else {
        setVoucherStats((prev) => ({
          ...prev,
          total: 0,
          available: 0,
          reserved: 0,
          used: 0
        }));
      }

      await fetchVoucherList(1, statusFilter, searchTerm);

      const deleted = data.deleted || 0;
      showSuccess(
        `Deleted ${deleted} voucher${deleted === 1 ? '' : 's'}${scope !== 'all' ? ` (${scope})` : ''}.`
      );
    } catch (error) {
      console.error('Failed to delete vouchers:', error);
      showError(`Failed to delete vouchers: ${error.response?.data?.error || error.message}`);
    } finally {
      setClearing(false);
    }
  };

  const handleDeleteSelectedVouchers = async (ids = []) => {
    if (!prize || !Array.isArray(ids) || ids.length === 0) return;
    setClearing(true);
    try {
      const { data } = await api.delete(
        `/admin/campaigns/${campaignId}/instant-win/prizes/${prize.id}/vouchers`,
        { data: { voucher_ids: ids, ...(isAllocationMode ? { allocation_id: allocationId } : {}) } }
      );

      if (!isAllocationMode && data.prize && typeof onPrizeUpdated === 'function') {
        onPrizeUpdated(data.prize);
      }

      if (data.voucherStats) {
        setVoucherStats({
          total: data.voucherStats.total || 0,
          available: data.voucherStats.available || 0,
          reserved: data.voucherStats.reserved || 0,
          used: data.voucherStats.used || 0,
          lastUploadedAt: data.voucherStats.lastUploadedAt || null
        });
      }

      await fetchVoucherList(pagination.page, statusFilter, searchTerm);
      setSelectedVoucherIds(new Set());

      const deletedCount = data.deleted || ids.length;
      showSuccess(`Deleted ${deletedCount} selected voucher${deletedCount === 1 ? '' : 's'}.`);
    } catch (error) {
      console.error('Failed to delete selected vouchers:', error);
      showError(`Failed to delete selected vouchers: ${error.response?.data?.error || error.message}`);
    } finally {
      setClearing(false);
    }
  };

  const handleDownloadTemplate = () => {
    const template = 'voucher_code\nVOUCHER-001\nVOUCHER-002\nVOUCHER-003\n';
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'instant-win-voucher-template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const startEditingVoucher = (voucher) => {
    setEditingVoucherId(voucher.id);
    setEditingVoucherCode(voucher.voucher_code || '');
  };

  const cancelEditingVoucher = () => {
    setEditingVoucherId(null);
    setEditingVoucherCode('');
    setSavingVoucherId(null);
  };

  const saveEditedVoucher = async () => {
    if (!prize || !editingVoucherId) return;
    const trimmed = editingVoucherCode.trim();
    if (!trimmed) {
      showError('Voucher code cannot be empty.');
      return;
    }

    setSavingVoucherId(editingVoucherId);
    try {
      const { data } = await api.put(
        `/admin/campaigns/${campaignId}/instant-win/prizes/${prize.id}/vouchers/${editingVoucherId}`,
        { voucher_code: trimmed }
      );

      if (data.voucher) {
        setVoucherList((prev) =>
          prev.map((voucher) =>
            voucher.id === data.voucher.id ? { ...voucher, ...data.voucher } : voucher
          )
        );
      }

      cancelEditingVoucher();
      showSuccess('Voucher code updated successfully.');
    } catch (error) {
      console.error('Failed to update voucher:', error);
      if (error?.response?.status === 409) {
        showError('A voucher with that code already exists for this prize.');
      } else {
        showError(`Failed to update voucher: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setSavingVoucherId(null);
    }
  };

  const rangeStartNumber = Number(rangeConfig.start);
  const rangeEndNumber = Number(rangeConfig.end);
  const rangeIsValid =
    selectedStrategy !== 'range' ||
    (!!rangeConfig.prefix.trim() &&
      Number.isFinite(rangeStartNumber) &&
      Number.isFinite(rangeEndNumber) &&
      rangeStartNumber > 0 &&
      rangeEndNumber >= rangeStartNumber);
  const selectedCount = selectedVoucherIds.size;
  const allSelected = voucherList.length > 0 && voucherList.every((voucher) => selectedVoucherIds.has(voucher.id));

  const isPage = variant === 'page';
  const isEmbedded = variant === 'embedded';

  if ((!open && !isPage && !isEmbedded) || !prize) {
    return null;
  }

  const confirmationOverlay = pendingConfirmation && (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-gray-900/50 px-4">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-2">
          {pendingConfirmation.type === 'delete' ? 'Delete Selected Vouchers' : 'Confirm Clear'}
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          {pendingConfirmation.type === 'delete'
            ? `Are you sure you want to delete ${pendingConfirmation.ids.length} selected voucher${pendingConfirmation.ids.length === 1 ? '' : 's'}? This action cannot be undone.`
            : pendingConfirmation.scope === 'available'
              ? 'Are you sure you want to remove all available vouchers?'
              : 'Are you sure you want to remove every voucher for this prize? This action cannot be undone.'}
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={closeConfirmation}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            disabled={clearing}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmAction}
            className={`px-4 py-2 text-sm text-white rounded-lg ${
              pendingConfirmation.type === 'delete'
                ? 'bg-red-600 hover:bg-red-500'
                : pendingConfirmation.scope === 'available'
                  ? 'bg-amber-600 hover:bg-amber-500'
                  : 'bg-red-600 hover:bg-red-500'
            }`}
            disabled={clearing}
          >
            {clearing
              ? pendingConfirmation.type === 'delete'
                ? 'Deleting…'
                : 'Clearing…'
              : pendingConfirmation.type === 'delete'
                ? 'Delete'
                : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );

  const containerClasses =
    [
      isEmbedded
        ? 'w-full'
        : isPage
          ? 'relative w-full max-w-5xl mx-auto bg-white rounded-3xl shadow-2xl'
          : 'relative w-full max-w-5xl bg-white rounded-xl shadow-2xl max-h-[calc(100vh-3rem)] overflow-hidden',
      className
    ].filter(Boolean).join(' ');
  const headerClasses = isEmbedded
    ? 'flex items-center justify-between pb-4'
    : isPage
      ? 'flex items-center justify-between border-b border-gray-200 px-8 py-6'
      : 'flex items-center justify-between border-b border-gray-200 px-6 py-4';
  const bodyClasses = [
    isEmbedded ? '' : isPage ? 'px-8 py-6' : 'px-6 py-5 overflow-y-auto max-h-[calc(100vh-7.5rem)]',
    'space-y-6'
  ]
    .filter(Boolean)
    .join(' ');

  const card = (
    <div className={containerClasses}>
      {showHeader && (
        <div className={headerClasses}>
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 tracking-tight">Manage Claim Codes</h3>
            <p className="text-sm text-gray-500 mt-1">
              {prize.prize_name} · Total quantity: {prize.total_quantity} · Remaining: {prize.remaining_quantity}
            </p>
          </div>
          {!isPage && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
      )}

      <div className={bodyClasses}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex items-center gap-3 text-gray-500">
              <svg className="animate-spin h-5 w-5 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              <span>Loading claim code settings…</span>
            </div>
          </div>
        ) : (
          <>
              {!isAllocationMode && (
              <section className="border border-gray-200 rounded-2xl p-6 bg-white">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-[0.2em] mb-4">
                  Strategy
                </h4>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="claim-code-strategy"
                        value="range"
                        checked={selectedStrategy === 'range'}
                        onChange={() => setSelectedStrategy('range')}
                        className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                      />
                      Range-based generation
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="claim-code-strategy"
                        value="list"
                        checked={selectedStrategy === 'list'}
                        onChange={() => setSelectedStrategy('list')}
                        className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                      />
                      CSV voucher list
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={handleStrategySave}
                    disabled={savingStrategy || !rangeIsValid}
                    className="inline-flex items-center px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg shadow-sm hover:bg-teal-500 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {savingStrategy ? 'Saving…' : 'Save Strategy'}
                  </button>
                </div>
                {selectedStrategy === 'range' ? (
                  <p className="mt-2 text-xs text-gray-500">
                    Provide a prefix and numeric range. Codes will be generated randomly within the range without reusing numbers.
                  </p>
                ) : (
                  <p className="mt-2 text-xs text-gray-500">
                    Upload a CSV file that contains a column named <code className="font-mono">voucher_code</code>, or a single column with codes. Codes are assigned randomly without duplicates.
                  </p>
                )}
              </section>
              )}

              {selectedStrategy === 'range' ? (
                <section className="border border-gray-200 rounded-2xl p-6 space-y-5 bg-white">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Claim Code Prefix*
                      </label>
                      <input
                        type="text"
                        value={rangeConfig.prefix}
                        onChange={(e) =>
                          setRangeConfig((prev) => ({ ...prev, prefix: e.target.value.toUpperCase() }))
                        }
                        placeholder="e.g., WIN"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Range Start*
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={rangeConfig.start}
                        onChange={(e) =>
                          setRangeConfig((prev) => ({ ...prev, start: e.target.value }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Range End*
                      </label>
                      <input
                        type="number"
                        min={rangeConfig.start || 1}
                        value={rangeConfig.end}
                        onChange={(e) =>
                          setRangeConfig((prev) => ({ ...prev, end: e.target.value }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  {rangeIsValid && selectedStrategy === 'range' && (
                    <div className="bg-gray-50 border border-dashed border-gray-200 rounded-lg p-4 text-sm text-gray-600">
                      <p className="font-medium text-gray-800 mb-1">Sample output</p>
                      <p className="font-mono">
                        {rangeConfig.prefix.toUpperCase()}
                        {String(Math.max(rangeStartNumber, 0)).padStart(5, '0')} –{' '}
                        {rangeConfig.prefix.toUpperCase()}
                        {String(Math.max(rangeEndNumber, rangeStartNumber)).padStart(5, '0')}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        Codes are assigned randomly across the range. Once exhausted the system falls back to random codes.
                      </p>
                    </div>
                  )}
                </section>
              ) : (
                <section className="border border-gray-200 rounded-2xl p-6 space-y-6 bg-white">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <div className="flex flex-col">
                        <span className="text-xs uppercase text-slate-500 tracking-wide">Total</span>
                        <span className="text-2xl font-semibold text-slate-800">
                          {voucherStats.total.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex-1 grid grid-cols-3 gap-3 text-center">
                        <div>
                          <p className="text-xs text-emerald-600">Available</p>
                          <p className="text-base font-medium text-emerald-700">
                            {voucherStats.available}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-amber-600">Reserved</p>
                          <p className="text-base font-medium text-amber-700">
                            {voucherStats.reserved}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600">Used</p>
                          <p className="text-base font-medium text-slate-700">
                            {voucherStats.used}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-white border border-gray-200 rounded-lg">
                      <p className="text-sm font-medium text-gray-700">Last upload</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {voucherStats.lastUploadedAt ? formatDateTime(voucherStats.lastUploadedAt) : 'No uploads yet'}
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        Codes are automatically marked as reserved when assigned and as used once the winner record is saved.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg shadow-sm hover:bg-teal-500 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <CloudUpload className="w-5 h-5" />
                        {uploading ? 'Uploading…' : 'Upload CSV'}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) {
                            handleVoucherUpload(file);
                          }
                          event.target.value = '';
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleDownloadTemplate}
                        className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        <Download className="w-5 h-5" />
                        CSV Template
                      </button>
                      <button
                        type="button"
                        onClick={() => fetchVoucherList(pagination.page, statusFilter, searchTerm)}
                        className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        <RefreshCw className="w-5 h-5" />
                        Refresh
                      </button>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={replaceExisting}
                        onChange={(e) => setReplaceExisting(e.target.checked)}
                        className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                      />
                      Replace existing vouchers before upload
                    </label>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <form
                      onSubmit={(event) => {
                        event.preventDefault();
                        fetchVoucherList(1, statusFilter, searchTerm);
                      }}
                      className="flex items-center gap-2"
                    >
                      <select
                        value={statusFilter}
                        onChange={(event) => {
                          const next = event.target.value;
                          setStatusFilter(next);
                          fetchVoucherList(1, next, searchTerm);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="all">All statuses</option>
                        <option value="available">Available</option>
                        <option value="reserved">Reserved</option>
                        <option value="used">Used</option>
                      </select>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search code…"
                        className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                      />
                      <button
                        type="submit"
                        className="px-3 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-500"
                      >
                        Search
                      </button>
                      {searchTerm && (
                        <button
                          type="button"
                          onClick={() => {
                            setSearchTerm('');
                            fetchVoucherList(1, statusFilter, '');
                          }}
                          className="px-3 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50"
                        >
                          Clear
                        </button>
                      )}
                    </form>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={requestDeleteSelected}
                        disabled={clearing || selectedCount === 0}
                        className="inline-flex items-center gap-1 px-3 py-2 border border-red-200 text-sm rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Selected
                        {selectedCount > 0 && (
                          <span className="ml-1 font-semibold">({selectedCount})</span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => requestClearVouchers('available')}
                        disabled={clearing}
                        className="inline-flex items-center gap-1 px-3 py-2 border border-amber-200 text-sm rounded-lg text-amber-700 hover:bg-amber-50 disabled:opacity-60"
                      >
                        <Trash2 className="w-5 h-5" />
                        Clear Available
                      </button>
                      <button
                        type="button"
                        onClick={() => requestClearVouchers('all')}
                        disabled={clearing}
                        className="inline-flex items-center gap-1 px-3 py-2 border border-red-200 text-sm rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-60"
                      >
                        <Trash2 className="w-5 h-5" />
                        Clear All
                      </button>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2">
                            <input
                              type="checkbox"
                              className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                              checked={voucherList.length > 0 && allSelected}
                              onChange={toggleSelectAll}
                              aria-label="Select all vouchers"
                            />
                          </th>
                          <th className="px-4 py-2 text-left font-semibold text-gray-600">Voucher Code</th>
                          <th className="px-4 py-2 text-left font-semibold text-gray-600">Status</th>
                          <th className="px-4 py-2 text-left font-semibold text-gray-600">Reserved</th>
                          <th className="px-4 py-2 text-left font-semibold text-gray-600">Used</th>
                          <th className="px-4 py-2 text-left font-semibold text-gray-600">Updated</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {listLoading ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                              Loading vouchers…
                            </td>
                          </tr>
                        ) : voucherList.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-6 text-center text-gray-400 text-sm">
                              No vouchers found for the current filters.
                            </td>
                          </tr>
                        ) : (
                          voucherList.map((voucher) => (
                            <tr key={voucher.id}>
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
                                  checked={selectedVoucherIds.has(voucher.id)}
                                  onChange={() => toggleSelectVoucher(voucher.id)}
                                  aria-label="Select voucher"
                                />
                              </td>
                              <td className="px-4 py-3">
                                {editingVoucherId === voucher.id ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={editingVoucherCode}
                                      onChange={(e) => setEditingVoucherCode(e.target.value)}
                                      className="px-2 py-1 border border-gray-300 rounded font-mono text-sm focus:ring-2 focus:ring-teal-500"
                                      disabled={savingVoucherId === voucher.id}
                                    />
                                    <button
                                      type="button"
                                      onClick={saveEditedVoucher}
                                      disabled={savingVoucherId === voucher.id}
                                      className="p-1 rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
                                      title="Save voucher code"
                                    >
                                      {savingVoucherId === voucher.id ? (
                                        <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                        </svg>
                                      ) : (
                                        <Check className="w-4 h-4" />
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={cancelEditingVoucher}
                                      disabled={savingVoucherId === voucher.id}
                                      className="p-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-60"
                                      title="Cancel"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-gray-800 break-all">{voucher.voucher_code}</span>
                                    <button
                                      type="button"
                                      onClick={() => startEditingVoucher(voucher)}
                                      className="p-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                                      title="Edit voucher code"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusBadgeClasses(
                                    voucher.status
                                  )}`}
                                >
                                  {STATUS_LABELS[voucher.status] || voucher.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-600 text-sm">{formatDateTime(voucher.reserved_at)}</td>
                              <td className="px-4 py-3 text-gray-600 text-sm">{formatDateTime(voucher.used_at)}</td>
                              <td className="px-4 py-3 text-gray-500 text-xs">{formatDateTime(voucher.updated_at)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <p className="text-xs text-gray-500">
                      Showing page {pagination.page} of {Math.max(pagination.totalPages, 1)} ·{' '}
                      {pagination.total.toLocaleString()} total vouchers
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={pagination.page <= 1 || listLoading}
                        onClick={() => fetchVoucherList(Math.max(pagination.page - 1, 1), statusFilter, searchTerm)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        disabled={pagination.page >= pagination.totalPages || listLoading}
                        onClick={() =>
                          fetchVoucherList(
                            Math.min(pagination.page + 1, pagination.totalPages || pagination.page + 1),
                            statusFilter,
                            searchTerm
                          )
                        }
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </section>
              )}
            </>
        )}
      </div>
    </div>
  );

  if (isPage || isEmbedded) {
    return (
      <>
        {card}
        {confirmationOverlay}
      </>
    );
  }

  const modalRoot = typeof document !== 'undefined'
    ? document.getElementById('modal-root') || document.body
    : null;

  if (!modalRoot) {
    return null;
  }

  const modalContent = (
    <>
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-gray-800 bg-opacity-60 px-4 py-6 sm:px-6 sm:py-8">
        {card}
      </div>
      {confirmationOverlay}
    </>
  );

  return ReactDOM.createPortal(modalContent, modalRoot);
};

export default ManageClaimCodes;
